import { ReactNode, useMemo } from "react";
import { CustomAccordion } from "@covertree/ui";
import { useFormContext } from "react-hook-form";
import { Box, Grid, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { InsuredTabFormValues } from "@redux/slices/endorsement";

interface SectionCardProps {
  title: string;
  dataTestId: string;
  fieldNames?: Array<keyof InsuredTabFormValues>;
  children: ReactNode;
}

/**
 * Collapsible endorsement section; flags an error state when any of its
 * `fieldNames` fail validation.
 */
export const SectionCard = ({
  title,
  children,
  dataTestId,
  fieldNames = [],
}: SectionCardProps) => {
  const {
    formState: { errors },
  } = useFormContext<InsuredTabFormValues>();

  const hasError = useMemo(
    () => fieldNames.some((name) => Boolean(errors[name])),
    [errors, fieldNames]
  );

  return (
    <Box
      position="relative"
      border="1px solid"
      borderRadius="20px"
      paddingX={3}
      paddingY={1}
      marginY={1}
      width={1}
      borderColor={hasError ? "error.main" : "action.disabled"}
      sx={(theme) => ({
        "& .MuiAccordion-root.Mui-expanded": { margin: 0 },
        "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: 48 },
        "& .MuiAccordionSummary-expandIconWrapper svg": {
          color: `${
            hasError ? theme.palette.error.main : theme.palette.text.secondary
          } !important`,
        },
      })}>
      <CustomAccordion
        dataTestId={dataTestId}
        arrowToRight
        withoutBorder
        contentSx={{ paddingX: 0 }}
        header={() => (
          <Grid container alignItems="center" gap={1} wrap="nowrap">
            <Typography
              variant="h5"
              fontSize={18}
              color={hasError ? "error.main" : undefined}>
              {title}
            </Typography>
            {hasError && (
              <Grid
                item
                display="flex"
                alignItems="center"
                gap={0.5}
                data-testid={`${dataTestId}-error`}>
                <ErrorOutlineIcon fontSize="small" color="error" />
              </Grid>
            )}
          </Grid>
        )}
        content={<Box pb={1}>{children}</Box>}
      />
    </Box>
  );
};
